import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { useMemo, useState } from "react";
import { DeleteModal } from "../../components";
import { useArticles } from "../../store/useArticles";
import { useDropLocations } from "../../store/useDropLocations";
import { useNavigate } from "react-router-dom";
import { PinArticleModal } from "./PinArticleModal";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

export const ArticlesTable = () => {
  /** Hooks */
  const { articles, deleteArticle, unpinArticle } = useArticles();
  const { dropLocations } = useDropLocations();

  // The dashboard listing carries pinned articles inline (each one knows where
  // it's pinned), so there's nothing to merge in from a separate fetch.
  const displayArticles = articles ?? [];
  const navigate = useNavigate();
  /** State */
  const [isOpen, setIsOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState<(typeof displayArticles)[0] | null>(
    null
  );

  const toggleModal = () => setIsOpen(!isOpen);

  // Pins store location ids; the table wants readable store names.
  const locationNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const location of dropLocations ?? []) {
      if (location?._id) {
        map[String(location._id)] =
          location.cost_place ?? location.address ?? String(location._id);
      }
    }
    return map;
  }, [dropLocations]);

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Title
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Home
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Author
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Published in
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Date
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Page views
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {displayArticles.map((item) => {
            const isPinned = Boolean(item?.isPinned);
            const pinTargets = [
              ...(item?.pinnedCities ?? []),
              ...(item?.pinnedLocationIds ?? []).map(
                (id) => locationNameById[String(id)] ?? "Store"
              ),
            ];
            return (
              <tr
                key={item._id}
                className={`transition ease-linear border-b cursor-pointer ${
                  isPinned
                    ? "bg-droppie-oranje/5 hover:bg-droppie-oranje/10"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="px-2 sm:px-6 py-1 sm:py-4 break-all sm:whitespace-nowrap"
                >
                  <span className="flex items-center gap-2 flex-wrap">
                    {item?.title}
                    {isPinned && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-droppie-oranje/20 text-droppie-oranje text-xs font-medium">
                        Pinned
                      </span>
                    )}
                    {pinTargets.map((target) => (
                      <span
                        key={target}
                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium"
                      >
                        {target}
                      </span>
                    ))}
                  </span>
                </td>
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap"
                >
                  {isPinned ? (
                    <span className="text-droppie-oranje font-medium">
                      Trending
                    </span>
                  ) : item?.homeSection === "NEWS" ? (
                    "News"
                  ) : (
                    "Droppie"
                  )}
                </td>
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.creator?.first_name} {item?.creator?.last_name}
                </td>
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {/* Articles target either cities or specific stores; showing
                      only cities made store-targeted ones look untargeted. */}
                  {[
                    ...(item?.cities ?? []),
                    ...(item?.locationIds ?? []).map(
                      (id) => locationNameById[String(id)] ?? "Store"
                    ),
                  ].join(", ") || (
                    <span className="text-gray-400">Nobody</span>
                  )}
                </td>
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {dayjs(item?.createdAt).fromNow()}
                </td>
                <td
                  onClick={() => navigate(`/edit-article/${item?._id}`)}
                  className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
                >
                  {item?.pageViews}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPinned) {
                        unpinArticle(item._id as string);
                        return;
                      }
                      setPinTarget(item);
                    }}
                    className={`font-satoshi text-xs font-medium px-2 py-1 rounded border ${
                      isPinned
                        ? "border-droppie-oranje text-droppie-oranje"
                        : "border-gray-border text-gray-600"
                    }`}
                  >
                    {isPinned ? "Unpin" : "Pin"}
                  </button>
                  <EditIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/edit-article/${item?._id}`);
                    }}
                    className="cursor-pointer"
                  />
                  <TrashIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      const confirmDelete = window.confirm(
                        "Are you sure you want to delete this article?"
                      );
                      if (confirmDelete) {
                        deleteArticle(item._id as string);
                      }
                    }}
                    className="cursor-pointer"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
      <PinArticleModal
        isOpen={Boolean(pinTarget)}
        article={pinTarget}
        onClose={() => setPinTarget(null)}
      />
    </div>
  );
};
